import type { CreateVolunteerDto } from '../api/volunteer';

const VOLUNTEER_SESSION_KEY = 'volunteer_session';

export interface VolunteerSessionData {
  id: string;
  application: CreateVolunteerDto;
  contractSigned: boolean;
  contractSignedAt?: string | undefined;
  signatureData?: string | undefined;
}

/**
 * Store the application temporarily in this browser tab. Nothing is persisted
 * by the backend until account creation succeeds.
 */
export function storeVolunteerSession(application: CreateVolunteerDto): string {
  const id = crypto.randomUUID();
  const sessionData: VolunteerSessionData = {
    id,
    application,
    contractSigned: false,
  };

  sessionStorage.setItem(VOLUNTEER_SESSION_KEY, JSON.stringify(sessionData));
  return id;
}

/**
 * Get volunteer session data from storage.
 */
export function getVolunteerSession(): VolunteerSessionData | null {
  const data = sessionStorage.getItem(VOLUNTEER_SESSION_KEY);
  if (!data) return null;

  try {
    return JSON.parse(data) as VolunteerSessionData;
  } catch {
    return null;
  }
}

/**
 * Update volunteer session data (e.g., after signing contract).
 */
export function updateVolunteerSession(
  updates: Partial<VolunteerSessionData>
): void {
  const current = getVolunteerSession();
  if (!current) return;

  const updated = { ...current, ...updates };
  sessionStorage.setItem(VOLUNTEER_SESSION_KEY, JSON.stringify(updated));
}

/**
 * Clear volunteer session data.
 */
export function clearVolunteerSession(): void {
  sessionStorage.removeItem(VOLUNTEER_SESSION_KEY);
}

/**
 * Check if the current session matches the given volunteer ID.
 */
export function hasVolunteerSession(volunteerId: string): boolean {
  const session = getVolunteerSession();
  return session?.id === volunteerId;
}
